import { Building2, Edit, Plus, RefreshCw, Search, Trash2, Upload, Users } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import AddInvestorSubscriptionModal from "../../components/admin/AddInvestorSubscriptionModal";
import AdminModalShell from "../../components/admin/AdminModalShell";
import BulkAddUsersModal from "../../components/admin/BulkAddUsersModal";
import EditSubscriptionModal from "../../components/admin/EditSubscriptionModal";
import ProviderSubscriptionsManager from "../../components/admin/ProviderSubscriptionsManager";
import { useToast } from "../../contexts/ToastContext";
import { adminProductsV3Service } from "../../services/adminProductsV3Service";
import httpService from "../../services/httpService";
import { subscriptionService } from "../../services/subscriptionService";
import usersService, { User } from "../../services/usersService";
import {
  getAccessSummary,
  SubscriptionSettings,
  SubscriptionWithRelations,
} from "../../types/subscription";
import { formatUtcDate } from "../../utils/dateUtils";
import ProviderNotificationsTab from "./ProviderNotificationsTab";

interface Provider {
  id: number;
  name: string;
}

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

const PAGE_SIZE = 20;

function isExternalSubscription(subscription: SubscriptionWithRelations): boolean {
  return subscription.subscriptionSettings.isSubscribedExternallyToProvider === true;
}

const AccessBadge: React.FC<{ ids: number[] }> = ({ ids }) => {
  const summary = getAccessSummary(ids);
  const className = summary === "All"
    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
    : summary === "None"
      ? "border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"
      : "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";

  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
      {summary}
    </span>
  );
};

const SourceBadge: React.FC<{ subscription: SubscriptionWithRelations }> = ({ subscription }) => (
  <span
    className={`inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
      isExternalSubscription(subscription)
        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
        : "border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"
    }`}
  >
    {isExternalSubscription(subscription) ? "External" : "In-app / Strategy"}
  </span>
);

const AdminSubscriptionsPage: React.FC = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<"notifications" | "subscriptions">("notifications");

  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const providerListRequestIdRef = useRef(0);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState<string | null>(null);
  const [userResults, setUserResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [subscriptions, setSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [allInvestorSubscriptions, setAllInvestorSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [allInvestorSubscriptionsUserId, setAllInvestorSubscriptionsUserId] = useState<string | null>(null);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsOnPage: 0,
    totalPages: 1,
    totalItems: 0,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionWithRelations | null>(null);
  const [providerPortfolios, setProviderPortfolios] = useState<ProviderItem[]>([]);
  const [providerBaskets, setProviderBaskets] = useState<ProviderItem[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const investorSubscriptionsRequestIdRef = useRef(0);
  const providerContentRequestIdRef = useRef(0);

  const loadProviders = useCallback(async () => {
    const requestId = ++providerListRequestIdRef.current;
    try {
      setProvidersLoading(true);
      setProvidersError(null);
      const response = await httpService.get<{ providers: Array<{ id: string | number; name: string }> }>("/providers");
      if (requestId !== providerListRequestIdRef.current) return;
      setProviders(
        (response.data.providers || [])
          .map((provider) => ({ id: Number(provider.id), name: provider.name }))
          .filter((provider) => Number.isInteger(provider.id) && provider.id > 0)
      );
    } catch (err) {
      if (requestId !== providerListRequestIdRef.current) return;
      setProvidersError("Unable to load providers. Please try again.");
      console.error("Failed to load providers:", err);
      toast.error("Failed to load providers");
    } finally {
      if (requestId === providerListRequestIdRef.current) setProvidersLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProviders();
    return () => { providerListRequestIdRef.current += 1; };
  }, [loadProviders]);

  const fetchInvestorSubscriptions = useCallback(async () => {
    if (!selectedUser) {
      investorSubscriptionsRequestIdRef.current += 1;
      setSubscriptions([]);
      setAllInvestorSubscriptions([]);
      setAllInvestorSubscriptionsUserId(null);
      setSubscriptionsError(null);
      setSubscriptionsLoading(false);
      return;
    }

    const requestId = investorSubscriptionsRequestIdRef.current + 1;
    investorSubscriptionsRequestIdRef.current = requestId;
    const userId = selectedUser.id;

    try {
      setSubscriptionsLoading(true);
      setSubscriptionsError(null);
      setAllInvestorSubscriptionsUserId(null);
      const [response, allSubscriptions] = await Promise.all([
        subscriptionService.listAdminInvestorSubscriptions(
          userId,
          page,
          PAGE_SIZE
        ),
        subscriptionService.listAdminInvestorSubscriptionsAll(userId),
      ]);

      if (investorSubscriptionsRequestIdRef.current !== requestId) return;

      setSubscriptions(response.items);
      setAllInvestorSubscriptions(allSubscriptions);
      setAllInvestorSubscriptionsUserId(userId);
      setPagination({
        currentPage: response.currentPage,
        itemsOnPage: response.itemsOnPage,
        totalPages: response.totalPages || 1,
        totalItems: response.totalItems,
      });
    } catch (err) {
      if (investorSubscriptionsRequestIdRef.current !== requestId) return;

      console.error("Failed to load investor subscriptions:", err);
      setSubscriptionsError("Failed to load investor subscriptions.");
      setAllInvestorSubscriptions([]);
      setAllInvestorSubscriptionsUserId(null);
    } finally {
      if (investorSubscriptionsRequestIdRef.current === requestId) {
        setSubscriptionsLoading(false);
      }
    }
  }, [page, selectedUser]);

  useEffect(() => {
    fetchInvestorSubscriptions();
  }, [fetchInvestorSubscriptions]);

  useEffect(() => {
    setPage(1);
  }, [selectedUser?.id]);

  const handleUserSearch = async () => {
    const query = userSearch.trim();
    if (!query) {
      setUserSearchError("Enter a name, email, or user ID.");
      return;
    }

    try {
      setUserSearchLoading(true);
      setUserSearchError(null);
      const response = await usersService.findUsers({
        searchString: query,
        pageNumber: 1,
        pageSize: 10,
      });
      setUserResults(response.items);
      if (response.items.length === 0) {
        setUserSearchError("No users found.");
      }
    } catch (err) {
      console.error("User search failed:", err);
      setUserSearchError("Failed to search users.");
    } finally {
      setUserSearchLoading(false);
    }
  };

  const loadProviderContent = async (providerId: number) => {
    const requestId = providerContentRequestIdRef.current + 1;
    providerContentRequestIdRef.current = requestId;

    try {
      setContentLoading(true);
      setProviderPortfolios([]);
      setProviderBaskets([]);
      const [portfolios, baskets] = await Promise.all([
        adminProductsV3Service.listAllPortfolios(providerId),
        adminProductsV3Service.listAllBaskets(providerId),
      ]);

      if (providerContentRequestIdRef.current !== requestId) return;

      setProviderPortfolios(
        portfolios
          .filter((portfolio) => portfolio.visibility === "SUBSCRIBER")
          .map((portfolio) => ({
            id: portfolio.id,
            title: portfolio.title,
            visibility: portfolio.visibility,
          }))
      );
      setProviderBaskets(
        baskets
          .filter((basket) => basket.visibility === "SUBSCRIBER")
          .map((basket) => ({
            id: basket.id,
            title: basket.title,
            visibility: basket.visibility,
          }))
      );
    } catch (err) {
      if (providerContentRequestIdRef.current !== requestId) return;

      console.error("Failed to load provider products:", err);
      setProviderPortfolios([]);
      setProviderBaskets([]);
    } finally {
      if (providerContentRequestIdRef.current === requestId) {
        setContentLoading(false);
      }
    }
  };

  const handleOpenEdit = async (subscription: SubscriptionWithRelations) => {
    setSelectedSubscription(subscription);
    setShowEditModal(true);
    await loadProviderContent(subscription.providerId);
  };

  const handleEditSubscription = async (
    subscriptionId: number,
    settings: SubscriptionSettings
  ): Promise<void> => {
    if (!selectedUser) return;
    await subscriptionService.updateInvestorSubscription(selectedUser.id, subscriptionId, {
      subscriptionSettings: settings,
    });
    toast.success("Subscription updated successfully");
    await fetchInvestorSubscriptions();
    setShowEditModal(false);
    setSelectedSubscription(null);
  };

  const handleAddInvestorSubscription = async (
    providerId: number,
    settings: SubscriptionSettings
  ): Promise<void> => {
    if (!selectedUser) return;

    const response = await subscriptionService.createInvestorSubscriptions(selectedUser.id, {
      subscriptions: [{ providerId, subscriptionSettings: settings }],
    });
    const result = response.results[0];
    if (!result?.success) {
      throw new Error(result?.error || "Failed to create subscription");
    }

    toast.success("Subscription added successfully");
    await fetchInvestorSubscriptions();
    setShowAddModal(false);
  };

  const handleDeleteSubscription = async () => {
    if (!selectedUser || !selectedSubscription) return;

    try {
      setDeleteLoading(true);
      const result = await subscriptionService.deleteInvestorSubscription(
        selectedUser.id,
        selectedSubscription.id
      );
      toast.success(
        result.action === "downgraded"
          ? "Subscription downgraded because active strategies exist"
          : "Subscription deleted successfully"
      );
      await fetchInvestorSubscriptions();
      setShowDeleteModal(false);
      setSelectedSubscription(null);
    } catch (err) {
      console.error("Failed to delete subscription:", err);
      toast.error("Failed to delete subscription");
    } finally {
      setDeleteLoading(false);
    }
  };

  const existingProviderIds = useMemo(
    () => allInvestorSubscriptions.map((subscription) => subscription.providerId),
    [allInvestorSubscriptions]
  );

  const investorSubscriptionGuardReady = selectedUser
    ? allInvestorSubscriptionsUserId === selectedUser.id
    : false;

  const selectedProvider = useMemo(
    () => providers.find((provider) => provider.id === selectedProviderId) || null,
    [providers, selectedProviderId]
  );

  return (
    <AdminPageLayout
      title="Subscriptions"
      description="View notification choices and manage provider subscriptions"
      actions={
        activeTab === "subscriptions" &&
        <button
          type="button"
          onClick={() => setShowBulkModal(true)}
          className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"
        >
          <Upload className="mr-2 h-4 w-4" />
          Sync Roster
        </button>
      }
    >
      <div role="tablist" aria-label="Subscription views" className="mb-4 inline-flex gap-1 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-rp-separator dark:bg-rp-surface">
        {(["notifications", "subscriptions"] as const).map(tab => (
          <button key={tab} id={`${tab}-tab`} role="tab" type="button"
            aria-selected={activeTab === tab} aria-controls={`${tab}-panel`} tabIndex={activeTab === tab ? 0 : -1}
            onClick={() => setActiveTab(tab)}
            onKeyDown={event => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const nextTab = event.key === "Home" ? "notifications" : event.key === "End" ? "subscriptions" : tab === "notifications" ? "subscriptions" : "notifications";
              setActiveTab(nextTab);
              document.getElementById(`${nextTab}-tab`)?.focus();
            }}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-rp-amber ${activeTab === tab ? "bg-rp-amber text-white" : "text-gray-500 dark:text-gray-400"}`}>
            {tab === "notifications" ? "Notifications" : "Subscriptions"}
          </button>
        ))}
      </div>
      <div id="notifications-panel" role="tabpanel" aria-labelledby="notifications-tab" hidden={activeTab !== "notifications"}>
        {activeTab === "notifications" && <ProviderNotificationsTab providers={providers} providersLoading={providersLoading} providersError={providersError} onRetryProviders={loadProviders} />}
      </div>
      <div id="subscriptions-panel" role="tabpanel" aria-labelledby="subscriptions-tab" hidden={activeTab !== "subscriptions"}>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-5 transition-colors duration-300 dark:border-rp-separator dark:bg-rp-surface">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-raised">
              <Building2 className="h-5 w-5 text-rp-amber" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight text-gray-900 dark:text-white">
                Provider Rosters
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Manage subscribers, imports, access, and external backfills.
              </p>
            </div>
          </div>

          <div className="mt-auto space-y-3">
            <select
              value={selectedProviderId ?? ""}
              onChange={(event) => setSelectedProviderId(event.target.value ? Number(event.target.value) : null)}
              disabled={providersLoading}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber disabled:opacity-60 dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
            >
              <option value="">Select a provider</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selectedProviderId}
              onClick={() => setShowProviderModal(true)}
              className="w-full rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Manage Provider Roster
            </button>
          </div>
        </section>

        <section className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-5 transition-colors duration-300 dark:border-rp-separator dark:bg-rp-surface">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-raised">
              <Users className="h-5 w-5 text-rp-amber" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight text-gray-900 dark:text-white">
                Investor Subscriptions
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Search one investor and manage provider access in a modal.
              </p>
            </div>
          </div>

          <div className="mt-auto space-y-3">
            <div className="flex min-h-[38px] items-center rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors dark:border-rp-separator dark:bg-rp-surface-raised">
              {selectedUser ? (
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                    {selectedUser.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {pagination.totalItems} subscription{pagination.totalItems === 1 ? "" : "s"} loaded
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No investor selected
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowInvestorModal(true)}
              className="w-full rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"
            >
              Manage Investor Subscriptions
            </button>
          </div>
        </section>
      </div>

      <AdminModalShell
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        title="Manage Provider Roster"
        description={selectedProvider?.name || "Select a provider roster to manage"}
        maxWidthClassName="max-w-7xl"
      >
        <ProviderSubscriptionsManager
          providerId={selectedProviderId}
          providerName={selectedProvider?.name}
        />
      </AdminModalShell>

      <AdminModalShell
        isOpen={showInvestorModal}
        onClose={() => setShowInvestorModal(false)}
        title="Manage Investor Subscriptions"
        description={selectedUser ? `${selectedUser.email} - ${pagination.totalItems} total` : "Search for an investor to manage provider access"}
        maxWidthClassName="max-w-6xl"
      >
        <div className="space-y-4">
          <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-rp-separator dark:bg-rp-surface">
            <div className="mb-3 flex items-center gap-2">
              <Search className="h-4 w-4 text-rp-amber" />
              <h3 className="text-base font-bold leading-tight text-gray-900 dark:text-white">
                Investor Lookup
              </h3>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={userSearch}
                onChange={(event) => {
                  setUserSearch(event.target.value);
                  setUserSearchError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleUserSearch();
                  }
                }}
                placeholder="Search users..."
                className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white dark:placeholder-gray-500"
              />
              <button
                type="button"
                onClick={handleUserSearch}
                disabled={userSearchLoading}
                className="rounded-lg bg-rp-amber px-3 py-2 text-white transition-colors hover:bg-yellow-600 disabled:opacity-50"
              >
                {userSearchLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </button>
            </div>
            {userSearchError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{userSearchError}</p>
            )}
            {userResults.length > 0 && (
              <div className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 dark:divide-rp-separator dark:border-rp-separator">
                {userResults.map((user) => (
                  <button
                    type="button"
                    key={user.id}
                    onClick={() => {
                      setSelectedUser(user);
                      setUserResults([]);
                    }}
                    className="block w-full bg-white px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:bg-black dark:hover:bg-rp-surface"
                  >
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      {user.email}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {user.name || "No name"} - {user.id}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface">
            <div className="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-rp-separator sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-bold leading-tight text-gray-900 dark:text-white">
                  Investor Subscriptions
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {selectedUser ? `${selectedUser.email} - ${pagination.totalItems} total` : "Select an investor to manage subscriptions"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                disabled={!selectedUser || !investorSubscriptionGuardReady || subscriptionsLoading}
                className="flex items-center justify-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Subscription
              </button>
            </div>

            {!selectedUser ? (
              <div className="py-24 text-center">
                <Users className="mx-auto mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Search for an investor to view subscriptions.
                </p>
              </div>
            ) : subscriptionsLoading ? (
              <div className="space-y-3 p-4">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
                ))}
              </div>
            ) : subscriptionsError ? (
              <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {subscriptionsError}
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="py-24 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  No subscriptions for this investor.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-rp-separator">
                  <thead className="bg-gray-50 dark:bg-rp-surface-sunken">
                    <tr>
                      {["Provider", "Portfolios", "Baskets", "Source", "Updated", "Actions"].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-rp-separator dark:bg-black">
                    {subscriptions.map((subscription) => (
                      <tr key={subscription.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-rp-surface">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {subscription.provider.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {subscription.providerId}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <AccessBadge ids={subscription.subscriptionSettings.portfolios} />
                        </td>
                        <td className="px-4 py-3">
                          <AccessBadge ids={subscription.subscriptionSettings.baskets} />
                        </td>
                        <td className="px-4 py-3">
                          <SourceBadge subscription={subscription} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatUtcDate(subscription.updatedAtUtc)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(subscription)}
                              className="rounded p-1 text-rp-amber transition-colors hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                              title="Edit subscription"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSubscription(subscription);
                                setShowDeleteModal(true);
                              }}
                              className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                              title="Delete subscription"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedUser && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 p-4 dark:border-rp-separator">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={pagination.currentPage <= 1 || subscriptionsLoading}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
                    disabled={pagination.currentPage >= pagination.totalPages || subscriptionsLoading}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </AdminModalShell>

      <BulkAddUsersModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => {
          if (selectedUser) {
            fetchInvestorSubscriptions();
          }
        }}
      />

      {selectedUser && (
        <AddInvestorSubscriptionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddInvestorSubscription}
          providers={providers}
          existingProviderIds={existingProviderIds}
        />
      )}

      {selectedSubscription && (
        <EditSubscriptionModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSubscription(null);
          }}
          onSubmit={handleEditSubscription}
          subscription={selectedSubscription}
          providerPortfolios={providerPortfolios}
          providerBaskets={providerBaskets}
          contentLoading={contentLoading}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSubscription(null);
        }}
        onConfirm={handleDeleteSubscription}
        title="Remove Subscription"
        message={`Remove ${selectedSubscription?.provider.name || "this provider"} from ${selectedUser?.email || "this investor"}?`}
        confirmText="Remove"
        isDestructive
        loading={deleteLoading}
      />
      </div>
    </AdminPageLayout>
  );
};

export default AdminSubscriptionsPage;
